import React, { useState } from 'react'
import {
  CardsContainer,
  ExternalLink,
  LabraToolTipV2,
  OfferCard,
  ProgressChipGroupV3,
  StatusChipBg,
  StatusChipText,
  StatusChipV3,
  VerticalDivider,
} from '@labrav/react-components'
import {
  BuyerDetailRow,
  BuyerInfo,
} from '../../../common/components/DetailRow/DetailRow'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faClone, faRepeat } from '@fortawesome/pro-regular-svg-icons'
import { useStyles } from './Contracts.styles'
import { getCloudImg } from '../../../common/utils/getCloudImg'
import { useNavigate, useParams } from 'react-router-dom'
import {
  numberFormat,
  standardLabraDateFormat,
  getRandom,
} from '@labrav/common'
import { DateTime } from 'luxon'
import renwalOff from '../../../common/images/renewalOff.svg'
import { useSelector } from '../../../store/hooks'

type OfferStatusState = 'sent' | 'started' | 'warning' | 'failed'

interface ChipsData {
  toolTipLabel?: string
  status: OfferStatusState
}

interface Status {
  statusText?: string
  statusColor: StatusChipBg
}

interface ContractCardProps {
  name: string
  Id: string
  contractIdentifier: string
  productId: string
  status: Status
  stages: ChipsData[]
  tcv: number
  offerType: string
  expirationDate: string
  productName: string | JSX.Element
  AccountOwner: string | JSX.Element
  increasing?: boolean
  autoRenew?: boolean
}

const ContractCard: React.FC<ContractCardProps> = ({
  name,
  Id,
  contractIdentifier,
  productId,
  status,
  stages,
  tcv,
  offerType,
  expirationDate,
  productName,
  AccountOwner,
  autoRenew,
}) => {
  const { classes } = useStyles()
  const navigate = useNavigate()
  const { accountId } = useParams<{
    accountId?: string
  }>()
  const timeZone = useSelector<string>(
    state => state.userProfile.userProfile?.timeZone || 'UTC'
  )
  const [isCopied, setIsCopied] = useState(false)

  const handleCopyClick = (text: string) => {
    navigator.clipboard.writeText(text)
    setIsCopied(true)
    setTimeout(() => setIsCopied(false), 2000)
  }

  const handleArrowClick = () => {
    if (contractIdentifier === 'subscription') {
      const subscriptionsPath = accountId
        ? `/flyout/aws/${accountId}/subscriptions/${Id}/detail`
        : `/flyout/subscriptions/${Id}/detail`
      navigate(subscriptionsPath)
    } else {
      const privateOffersPath = accountId
        ? `/flyout/aws/${accountId}/products-listing/${productId}/private-offers/${Id}/details`
        : `/flyout/products-listing/${productId}/private-offers/${Id}/details`
      navigate(privateOffersPath)
    }
  }

  const renewalDate = DateTime.fromISO(expirationDate, {
    zone: timeZone,
  })
  const currentDate = DateTime.now().setZone(timeZone)
  const daysUntilRenewal = Math.floor(
    renewalDate.diff(currentDate, 'days').days
  )

  const renderRenewalValue = () => {
    const isSubscription = contractIdentifier === 'subscription'
    const endedText = isSubscription ? 'Contract ended' : 'Offer expired'
    const autoRenewalText = isSubscription
      ? `Note: Auto-renewal is ${autoRenew ? 'on' : 'off'} `
      : ''

    let icon = (
      <img
        src={renwalOff}
        data-testid={'renewal-icon'}
        className={classes.renewalOffIcon}
      />
    )

    if (autoRenew) {
      icon = <FontAwesomeIcon icon={faRepeat} className={classes.renewalIcon} />
    }

    if (daysUntilRenewal < 0) {
      return isSubscription ? (
        status.statusText === 'Contract started' ? (
          <>--</>
        ) : (
          <>
            <LabraToolTipV2
              tooltipPlacement="top"
              label={
                <>
                  <div>{renewalDate.toFormat(standardLabraDateFormat)}</div>{' '}
                  <div>{endedText} on</div>
                </>
              }
            >
              <div className={classes.contractRed}>
                {icon}
                {renewalDate.toFormat(standardLabraDateFormat)}
              </div>
            </LabraToolTipV2>
          </>
        )
      ) : (
        <LabraToolTipV2
          tooltipPlacement="top"
          label={
            <>
              <div>{renewalDate.toFormat(standardLabraDateFormat)}</div>{' '}
              <div>{endedText} on</div>
            </>
          }
        >
          <div className={classes.contractRed}>
            {icon}
            {renewalDate.toFormat(standardLabraDateFormat)}
          </div>
        </LabraToolTipV2>
      )
    } else if (daysUntilRenewal <= (isSubscription ? 120 : 5)) {
      return (
        <LabraToolTipV2
          tooltipPlacement="top"
          label={
            <>
              <div>{renewalDate.toFormat(standardLabraDateFormat)}</div>{' '}
              <div>{autoRenewalText}</div>
            </>
          }
        >
          <div
            className={
              daysUntilRenewal <= (isSubscription ? 90 : 3)
                ? classes.contractRed
                : classes.contractOrange
            }
          >
            {isSubscription ? icon : null}
            {daysUntilRenewal} {daysUntilRenewal === 1 ? 'day' : 'days'}
          </div>
        </LabraToolTipV2>
      )
    }

    const formattedDate = renewalDate.toFormat(standardLabraDateFormat)

    return (
      <LabraToolTipV2
        tooltipPlacement="top"
        label={
          <>
            <div>{renewalDate.toFormat(standardLabraDateFormat)}</div>{' '}
            <div>
              {isSubscription
                ? `Note: Auto-renewal is ${autoRenew ? 'on' : 'off'} `
                : 'Offer expiring on'}
            </div>
          </>
        }
      >
        {' '}
        <div>
          {isSubscription ? icon : null}
          {formattedDate}
        </div>
      </LabraToolTipV2>
    )
  }

  const getRenewalTitle = () => {
    const isSubscription = contractIdentifier === 'subscription'
    const baseTitle = isSubscription ? 'Contract ending' : 'Offer expiring'

    if (daysUntilRenewal < 0) {
      return isSubscription
        ? status.statusText === 'Contract started'
          ? 'Contract ending in'
          : 'Contract ended'
        : 'Offer expired'
    } else if (daysUntilRenewal <= (isSubscription ? 120 : 5)) {
      return `${baseTitle} in`
    } else {
      return `${baseTitle} on`
    }
  }

  const randomKey = getRandom()
  return (
    <CardsContainer
      key={`contract-${Id}-${name}-${randomKey}-card`}
      hoverEffect={true}
      handleClick={handleArrowClick}
    >
      <OfferCard
        key={`contract-${Id}-${name}-${randomKey}`}
        name={
          <div className={classes.name}>
            <div className={classes.text}>{name}</div>
            <LabraToolTipV2
              tooltipPlacement="top"
              label={
                contractIdentifier === 'subscription'
                  ? 'Labra subscription ID'
                  : 'Labra offer ID'
              }
            >
              <div className={classes.account}>{Id}</div>
            </LabraToolTipV2>

            <LabraToolTipV2
              tooltipPlacement="top"
              label={isCopied ? 'Copied' : ''}
            >
              <FontAwesomeIcon
                icon={faClone}
                className={classes.copyIcon}
                onClick={e => {
                  e.stopPropagation()
                  handleCopyClick(Id)
                }}
                style={{
                  height: '16px',
                  width: '16px',
                  color: isCopied ? 'green' : 'inherit',
                }}
              />
            </LabraToolTipV2>
          </div>
        }
        onArrowClick={handleArrowClick}
        isLastCard={true}
        customiseForBuyer={true}
        hideLogo={true}
        rightSideSection={
          <div
            onClick={e => e.stopPropagation()}
            className={classes.rightSideContainer}
          >
            {contractIdentifier === 'subscription' &&
            expirationDate &&
            status.statusText !== 'Contract unsubscribed' ? (
              <ExternalLink
                href={
                  accountId
                    ? `/flyout/aws/${accountId}/subscriptions/${Id}/edit`
                    : `/flyout/subscriptions/${Id}/edit`
                }
              >
                Create ABO
              </ExternalLink>
            ) : (
              contractIdentifier === 'private_offer' &&
              (status.statusText !== 'offer cancelled' || 'offer accepted') && (
                <ExternalLink
                  href={
                    accountId
                      ? `/flyout/aws/${accountId}/products-listing/${productId}/private-offers/${Id}/details`
                      : `/flyout/products-listing/${productId}/private-offers/${Id}/details`
                  }
                >
                  Extend expiration date
                </ExternalLink>
              )
            )}
            <StatusChipV3
              bgColor={status.statusColor || StatusChipBg.GREEN}
              textColor={StatusChipText.GRAY}
              toolTipLabel={status.statusText || ''}
              label={status.statusText || ''}
            />
            <ProgressChipGroupV3 chips={stages} />
            <VerticalDivider height="24px"></VerticalDivider>
            <img
              src={getCloudImg('AWS')}
              data-testid={`icon1-${1}`}
              style={{ width: '24px', height: '24px' }}
            />
          </div>
        }
        actionTooltip="View details"
      />
      <BuyerDetailRow
        key={`contract-${Id}-${name}-buyer-detail-row-${randomKey}`}
        children={
          <>
            <BuyerInfo
              title={'TCV'}
              value={
                <LabraToolTipV2
                  tooltipPlacement="top"
                  label="Total contract value"
                >
                  <div>
                    <span className={classes.currency}>$</span>
                    <span>{numberFormat(tcv, 3) || '-'}</span>
                  </div>
                </LabraToolTipV2>
              }
              gridSize={2}
            />
            {offerType && (
              <BuyerInfo
                title={'Offer type'}
                value={<div>{offerType}</div>}
                gridSize={2}
              />
            )}
            {expirationDate ? (
              <BuyerInfo
                title={getRenewalTitle()}
                value={renderRenewalValue()}
                gridSize={2}
              />
            ) : (
              <BuyerInfo
                title={'Contract ending on'}
                value={<div>--</div>}
                gridSize={2}
              />
            )}

            <BuyerInfo
              title={'Product name'}
              value={productName}
              gridSize={4}
            />

            <BuyerInfo
              title={'Account owner'}
              value={
                <LabraToolTipV2
                  tooltipPlacement="top"
                  label="Person who created the offer"
                >
                  {AccountOwner}
                </LabraToolTipV2>
              }
              showDivider={false}
              gridSize={2}
            />
          </>
        }
      />
    </CardsContainer>
  )
}

export default ContractCard
